import { useCallback, useMemo } from "react";
import { isMobile } from "react-device-detect";
import { useLocation } from "react-router";

import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getListedCollections } from "domains/collection/selectors";
import {
  buildCreate,
  buildDiscover,
  buildHome,
  buildModerate,
  buildMonitor,
  buildMore,
} from "domains/nav/helpers/buildMenuItems";
import { extractSubnavTerms } from "domains/nav/helpers/extractSubnavTerms";
import { getActiveItemId } from "domains/nav/helpers/getActiveItemId";
import type { SubnavMenuItemInput } from "domains/nav/types/hostCommand";
import { useTheme } from "domains/theme/useTheme";
import { showWhatsNewIcon } from "domains/whatsNew/selectors";
import { getCanDisplayBookmarks, getCanEditSources, getCanViewSources } from "entities/permissions/selectors";

export const useSubnavMenuItems = () => {
  const { pathname } = useLocation();
  const canDisplayBookmarks = useSelector(getCanDisplayBookmarks);
  const customTopicCount = useSelector((state) => state.topic.customTopicsCount);
  const canViewSources = useSelector(getCanViewSources);
  const canEditSources = useSelector(getCanEditSources);
  const listed = useSelector(getListedCollections);
  const showWhatsNew = useSelector(showWhatsNewIcon);
  const theme = useTheme();
  const translate = useTranslate();
  const subnavTerms = extractSubnavTerms(translate);

  const buildMenuItems = useCallback(
    (isAuthenticated: boolean): SubnavMenuItemInput[] => {
      const items: SubnavMenuItemInput[] = [
        buildHome(subnavTerms.home),
        buildDiscover(subnavTerms.discover, listed, customTopicCount),
        buildMonitor(subnavTerms.monitor),
        buildCreate(subnavTerms.create),
        buildMore(subnavTerms.more, isAuthenticated, showWhatsNew, theme, canDisplayBookmarks),
      ];

      const activeItemId = getActiveItemId(pathname);
      const activeItem = activeItemId === undefined ? undefined : items.find(({ id }) => id === activeItemId);
      if (activeItem !== undefined && activeItem.divider === undefined) activeItem.active = true;
      return items;
    },
    [
      canDisplayBookmarks,
      customTopicCount,
      listed,
      pathname,
      showWhatsNew,
      subnavTerms.create,
      subnavTerms.discover,
      subnavTerms.home,
      subnavTerms.monitor,
      subnavTerms.more,
      theme,
    ],
  );

  const adminMenuItems = useMemo(() => {
    if (isMobile) return [];
    if (!canViewSources || !canEditSources) return [];

    return [buildModerate(subnavTerms.moderate)];
  }, [canViewSources, canEditSources, subnavTerms.moderate]);

  const userMenuItems = useMemo(() => [...buildMenuItems(true), ...adminMenuItems], [adminMenuItems, buildMenuItems]);

  const publicMenuItems = useMemo(() => buildMenuItems(false), [buildMenuItems]);

  return { publicMenuItems, userMenuItems };
};
